import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { omit } from 'lodash-es';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  useBillingBillingSettingsPartialUpdateMutation,
  useBillingBillingSettingsRetrieveQuery,
  useOrganizationSettingsPartialUpdateMutation,
  useOrganizationSettingsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { columnOptions } from '../invoices/modals/DownloadInvoices/steps/components/InvoiceColumnSelect';
import BillingSettingsForm, {
  BillingSettingsFormValues,
} from './BillingSettingsForm';
import { PAYMENT_LINK_EMAIL_TEMPLATE_DEFAULT } from './constants';

export default function BillingSettingsScreen() {
  const dispatch = useAppDispatch();
  const form = useForm<BillingSettingsFormValues>({
    defaultValues: {
      payment_link_email_template: PAYMENT_LINK_EMAIL_TEMPLATE_DEFAULT,
      invoiceGenerationOptions: columnOptions,
    },
  });

  const { data: currentSettings, isLoading } =
    useBillingBillingSettingsRetrieveQuery({});

  const { data: orgSettings, isLoading: isLoadingOrgSettings } =
    useOrganizationSettingsRetrieveQuery({});

  useEffect(() => {
    if (isLoading || isLoadingOrgSettings) {
      return;
    }

    const columnSettings =
      currentSettings?.invoice_generation_columns?.split(',') || [];
    const invoiceGenerationOptions = columnOptions.filter((option) =>
      columnSettings.includes(option.value)
    );

    form.reset({
      ...currentSettings,
      payment_link_email_template:
        currentSettings?.payment_link_email_template ||
        PAYMENT_LINK_EMAIL_TEMPLATE_DEFAULT,
      billing_address: orgSettings?.billing_address,
      tax_id: orgSettings?.tax_id,
      invoiceGenerationOptions,
    });
  }, [
    isLoading,
    currentSettings,
    form,
    isLoadingOrgSettings,
    orgSettings?.billing_address,
    orgSettings?.tax_id,
  ]);

  const [updateBillingSettings] =
    useBillingBillingSettingsPartialUpdateMutation();

  const [updateOrgSettings] = useOrganizationSettingsPartialUpdateMutation();

  const handleSubmit = useCallback<SubmitHandler<BillingSettingsFormValues>>(
    async (data) => {
      const billing_address = data.billing_address;

      const invoiceGenerationOptions = data.invoiceGenerationOptions
        .map((option) => option.value)
        .join(',');

      const billingSettingsUpdate = {
        ...omit(data, ['invoiceGenerationOptions', 'billing_address']),
        phone_number: data.phone_number ?? '',
        invoice_generation_columns: invoiceGenerationOptions,
      };

      try {
        await Promise.all([
          updateBillingSettings({
            patchedBillingSettings: billingSettingsUpdate,
          }).unwrap(),
          updateOrgSettings({
            patchedOrganizationSettings: {
              billing_address,
              tax_id: data.tax_id,
            },
          }).unwrap(),
        ]);

        dispatch(
          show({
            type: 'success',
            title: 'Updated billing settings',
          })
        );
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to update billing settings',
            description: formatServerError(e),
          })
        );
      }
    },
    [dispatch, updateBillingSettings, updateOrgSettings]
  );

  return (
    <FormProvider {...form}>
      {isLoading || isLoadingOrgSettings ? (
        <LoadingIndicator />
      ) : (
        <div>
          <BillingSettingsForm
            minimumInvoiceNumber={currentSettings?.invoice_seed_number ?? 1}
          />
        </div>
      )}
      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          variant="primary"
          onClick={form.handleSubmit(handleSubmit)}
        >
          Save
        </Button>
      </div>
    </FormProvider>
  );
}
